import { IconX } from '@tabler/icons-react';
import { useEffect, useId, useState, type RefObject } from 'react';
import './Tour.css';

/** Completion belongs to the destination; hiding the prompt does not complete it. */
export function Tour({
  scope,
  target,
  desktopAnchor,
  note,
  instruction,
  step,
  completed,
  onHide,
  onShown,
  onGo,
  approach = 'side',
}: {
  scope: RefObject<HTMLDivElement | null>;
  target: string;
  desktopAnchor?: string;
  note: string;
  instruction: string;
  step: 1 | 2;
  completed: boolean;
  onHide: () => void;
  onShown?: () => void;
  onGo?: () => void;
  approach?: 'side' | 'top' | 'bottom';
}) {
  const maskId = useId();
  const [position, setPosition] = useState<{
    left: number;
    top: number;
    width: number;
    wide: boolean;
    hole: { x: number; y: number; width: number; height: number };
    coachTop: number;
  } | null>(null);

  useEffect(() => {
    if (completed) return;
    const root = scope.current;
    if (!root) return;
    let element: HTMLElement | null = null;

    function place() {
      const found = root!.querySelector<HTMLElement>(target);
      if (found !== element) {
        if (element) observer.unobserve(element);
        element = found;
        if (element) observer.observe(element);
      }
      if (!element || !element.closest('main')) {
        setPosition(null);
        return;
      }
      const bounds = root!.getBoundingClientRect();
      const button = element!.getBoundingClientRect();
      const anchor = element!.querySelector<HTMLElement>(
        desktopAnchor ?? ':scope',
      );
      const pointer = anchor?.getBoundingClientRect() ?? button;
      if (button.bottom <= 0 || button.top >= window.innerHeight) {
        setPosition(null);
        return;
      }
      const room = window.innerWidth - button.right - 30;
      const wide =
        window.innerWidth >= 1100 && (approach !== 'side' || room >= 220);
      const width = approach !== 'side' ? 280 : Math.min(280, room);
      const center = button.left - bounds.left + button.width / 2;
      let top = pointer.top - bounds.top + pointer.height / 2 - 24;
      if (approach === 'top') top = button.bottom - bounds.top + 14;
      if (approach === 'bottom') top = button.top - bounds.top - 14;
      setPosition({
        left:
          approach !== 'side'
            ? center - width / 2
            : button.right - bounds.left + 14,
        top,
        width,
        wide,
        hole: {
          x: button.left - 4,
          y: button.top - 4,
          width: button.width + 8,
          height: button.height + 8,
        },
        coachTop:
          button.bottom + 196 < window.innerHeight
            ? button.bottom + 20
            : Math.max(16, button.top - 196),
      });
    }

    const observer = new ResizeObserver(place);
    observer.observe(root);
    const mutations = new MutationObserver(place);
    mutations.observe(root, { childList: true, subtree: true });
    place();
    window.addEventListener('resize', place);
    window.addEventListener('scroll', place, true);
    return () => {
      observer.disconnect();
      mutations.disconnect();
      window.removeEventListener('resize', place);
      window.removeEventListener('scroll', place, true);
    };
  }, [scope, target, desktopAnchor, note, completed, approach]);

  const shown = !completed && position !== null;
  useEffect(() => {
    if (shown) onShown?.();
  }, [shown, onShown]);

  if (completed || !position) return null;
  if (!position.wide)
    return (
      <>
        <svg className="tour-spotlight" aria-hidden>
          <defs>
            <mask
              id={maskId}
              maskUnits="userSpaceOnUse"
              x="0"
              y="0"
              width="100%"
              height="100%"
            >
              <rect width="100%" height="100%" fill="white" />
              <rect {...position.hole} rx={12} fill="black" />
            </mask>
          </defs>
          <rect
            width="100%"
            height="100%"
            className="tour-dim"
            mask={`url(#${maskId})`}
          />
          <rect {...position.hole} rx={12} className="tour-spotlight-ring" />
        </svg>
        <aside
          className="tour-coach"
          style={{ top: position.coachTop }}
          aria-label="Tour"
          aria-live="polite"
        >
          <div className="tour-coach-heading">
            <h2>{note}</h2>
            <button type="button" aria-label="Hide tour" onClick={onHide}>
              <IconX size={20} aria-hidden />
            </button>
          </div>
          <p>{instruction}</p>
          <div className="tour-progress" aria-label={`Step ${step} of 2`}>
            <span className="tour-progress-done" />
            <span className={step === 2 ? 'tour-progress-done' : undefined} />
          </div>
        </aside>
      </>
    );

  return (
    <aside
      className={`tour-callout tour-callout-${approach}`}
      style={{ left: position.left, top: position.top, width: position.width }}
      aria-label="Tour"
      aria-live="polite"
    >
      <h2>{note}</h2>
      <div className="tour-callout-footer">
        <button type="button" className="tour-skip" onClick={onHide}>
          Hide tour
        </button>
        <span className="tour-step">{step} of 2</span>
        <button
          type="button"
          className="tour-go"
          onClick={() =>
            onGo
              ? onGo()
              : scope.current?.querySelector<HTMLElement>(target)?.click()
          }
        >
          Go here
        </button>
      </div>
    </aside>
  );
}
